import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  makeStyles,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Box,
  Link,
} from '@material-ui/core';
import WebhookIcon from '@material-ui/icons/SettingsInputAntenna';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { toast } from 'react-toastify';
import useWebhookConfiguration from '../../hooks/useWebhookConfiguration';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: '25px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '10px',
    color: theme.palette.primary.main,
  },
  content: {
    flex: 1,
    marginBottom: '15px',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  statusIcon: {
    marginRight: '8px',
    fontSize: '20px',
  },
  configuredIcon: {
    color: '#4caf50',
  },
  notConfiguredIcon: {
    color: '#f44336',
  },
  warningIcon: {
    color: '#ff9800',
  },
  urlField: {
    marginTop: '15px',
    marginBottom: '15px',
  },
  secretContainer: {
    marginTop: '15px',
    padding: '12px',
    backgroundColor: theme.palette.grey[100],
    borderRadius: '4px',
  },
  secretLabel: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginBottom: '4px',
  },
  secretValue: {
    fontFamily: 'monospace',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    wordBreak: 'break-all',
  },
  button: {
    marginTop: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60px',
  },
  chip: {
    marginTop: '10px',
  },
  disabledChip: {
    backgroundColor: '#ff9800',
    color: '#fff',
  },
  description: {
    marginTop: '10px',
  },
  headerInfo: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: theme.palette.grey[50],
    borderRadius: '4px',
    fontSize: '13px',
  },
  inactiveAlert: {
    marginTop: '15px',
    padding: '12px',
    backgroundColor: '#fff3e0',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  inactiveAlertText: {
    fontSize: '13px',
    color: '#e65100',
  },
  docLink: {
    marginTop: '10px',
    fontSize: '13px',
  },
}));

const WebhookConfigurationCard = () => {
  const classes = useStyles();
  const {
    loading,
    saving,
    webhook,
    isConfigured,
    isActive,
    saveWebhook,
    deleteWebhook,
    getPartialSecret,
  } = useWebhookConfiguration();

  const [webhookUrl, setWebhookUrl] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    if (webhook?.webhook_url) {
      setWebhookUrl(webhook.webhook_url);
    } else {
      setWebhookUrl('');
    }
  }, [webhook]);

  const validateUrl = (url) => {
    if (!url) {
      return 'La URL es requerida';
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') {
        return 'La URL debe usar HTTPS';
      }
      return '';
    } catch {
      return 'URL inválida';
    }
  };

  const handleSave = () => {
    const error = validateUrl(webhookUrl);
    if (error) {
      setUrlError(error);
      return;
    }
    setUrlError('');
    saveWebhook(webhookUrl);
  };

  const handleCopySecret = () => {
    if (webhook?.secret) {
      navigator.clipboard.writeText(webhook.secret);
      toast.success('Secret copiado al portapapeles');
    }
  };

  const handleUrlChange = (e) => {
    setWebhookUrl(e.target.value);
    if (urlError) {
      setUrlError(validateUrl(e.target.value));
    }
  };

  // Si el webhook está desactivado por Faster, no se permite editar
  const isDisabled = isConfigured && !isActive;

  if (loading) {
    return (
      <Paper className={classes.paper}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography className={classes.title}>
        <WebhookIcon className={classes.icon} />
        Configuración de Webhook
      </Typography>

      <div className={classes.content}>
        <div className={classes.status}>
          {isConfigured ? (
            isActive ? (
              <>
                <CheckCircleIcon className={`${classes.statusIcon} ${classes.configuredIcon}`} />
                <Typography>Webhook activo</Typography>
              </>
            ) : (
              <>
                <WarningIcon className={`${classes.statusIcon} ${classes.warningIcon}`} />
                <Typography>Webhook desactivado</Typography>
              </>
            )
          ) : (
            <>
              <ErrorIcon className={`${classes.statusIcon} ${classes.notConfiguredIcon}`} />
              <Typography>No configurado</Typography>
            </>
          )}
        </div>

        {isDisabled && (
          <div className={classes.inactiveAlert}>
            <WarningIcon style={{ color: '#e65100', fontSize: '20px' }} />
            <Typography className={classes.inactiveAlertText}>
              Tu webhook fue desactivado por Faster. Esto puede ocurrir si el endpoint 
              no responde correctamente en reiteradas ocasiones. Contactá a soporte para reactivarlo.
            </Typography>
          </div>
        )}

        {!isConfigured && (
          <Typography variant="body2" color="textSecondary" className={classes.description}>
            Configurá un webhook para recibir eventos de Faster en tiempo real
            (nuevos pedidos, actualizaciones de estado, etc.).
          </Typography>
        )}

        <Typography variant="body2" className={classes.docLink}>
          <Link
            href={`${process.env.REACT_APP_BASE_URL_FASTER_API_V2}/api/documentation#/?id=📚-webhooks-de-negocios`}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            Ver documentación de configuración de webhooks
          </Link>
        </Typography>

        <TextField
          label="URL del Webhook"
          placeholder="https://tu-dominio.com/webhook/faster"
          variant="outlined"
          fullWidth
          className={classes.urlField}
          value={webhookUrl}
          onChange={handleUrlChange}
          error={!!urlError}
          helperText={urlError || 'Debe ser una URL HTTPS válida'}
          disabled={isDisabled}
          InputProps={{
            readOnly: isDisabled,
          }}
        />

        {isConfigured && webhook?.secret && (
          <>
            <div className={classes.secretContainer}>
              <Typography className={classes.secretLabel}>
                Secret (Header: X-Faster-Secret)
              </Typography>
              <div className={classes.secretValue}>
                <span>{showSecret ? webhook.secret : getPartialSecret()}</span>
                <Box>
                  <Tooltip title={showSecret ? 'Ocultar' : 'Mostrar'}>
                    <IconButton size="small" onClick={() => setShowSecret(!showSecret)}>
                      {showSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copiar">
                    <IconButton size="small" onClick={handleCopySecret}>
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </div>
            </div>

            <Typography variant="body2" color="textSecondary" className={classes.headerInfo}>
              Usá este secret para validar que las peticiones provienen de Faster.
              Lo recibirás en el header <code>X-Faster-Secret</code> de cada request.
            </Typography>
          </>
        )}

        {isConfigured && (
          <Chip
            label={isActive ? 'Activo' : 'Desactivado por Faster'}
            size="small"
            className={`${classes.chip} ${!isActive ? classes.disabledChip : ''}`}
            color={isActive ? 'primary' : 'default'}
          />
        )}
      </div>

      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || isDisabled || !webhookUrl}
        >
          {saving ? (
            <CircularProgress size={24} color="inherit" />
          ) : isConfigured ? (
            'Actualizar webhook'
          ) : (
            'Configurar webhook'
          )}
        </Button>
        
        {isConfigured && isActive && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={deleteWebhook}
            disabled={saving}
          >
            Eliminar webhook
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default WebhookConfigurationCard;

